/**
 * Things you can carry (M3-03).
 *
 * The first item system in the game, and it arrives here rather than in a
 * ticket of its own because foraging is the first thing that produces
 * something to hold. It is kept deliberately small — a stack, a weight, and a
 * date of picking — and the shape is chosen against what M4-03 will ask of it
 * rather than against what an item system could be:
 *
 * - **Stacks, not objects.** Forty yarrow leaves are one row with a count, not
 *   forty rows. Anything that needs per-instance state (a notched blade, a
 *   named sword) is not stackable and gets its own row; that distinction is a
 *   flag on the definition rather than two parallel systems.
 * - **Freshness is derived, never stored.** A stack records the tick it was
 *   gathered on, and how fresh it is now is arithmetic. The alternative — a
 *   `freshness` number decremented by somebody every tick — is a number that
 *   goes wrong across a server restart that skipped an hour, and is 65,536
 *   subtractions a second nobody asked for. This is the same call `ecology.ts`
 *   makes about carcasses and `wound.ts` makes about clocks.
 * - **Definitions are content, stacks are world state.** One `YARROW` record
 *   is every yarrow leaf in the valley; picking one may not mutate it. The
 *   rule that keeps that honest is that nothing here holds an `ItemDef` — the
 *   sim carries `defId` strings and asks the catalogue.
 *
 * What is *not* here, deliberately: containers, durability, and money. Taking
 * a carried weapon in hand is a loadout operation; inventory stacks remain
 * deliberately ignorant of equipment state.
 */
import { TICKS_PER_GAME_DAY } from './time.js';
export function createInventory() {
    return { items: [] };
}
/* -------------------------------------------------------------------------- *
 * Freshness
 * -------------------------------------------------------------------------- */
/**
 * Named stages, for the reason stamina has bands and a carcass has stages:
 * a player should read one word, not a percentage. M4-03 asks the *stage*,
 * never the fraction — "spent" is a rule, 0.97 is a number.
 */
export const FRESHNESS_STAGES = [
    { id: 'fresh', until: 0.4 },
    { id: 'wilting', until: 0.75 },
    { id: 'old', until: 1 },
    { id: 'spent', until: Infinity },
];
/** How far through its shelf life a stack is. 0 for things that keep. */
export function ageOf(def, stack, tick) {
    if (def.shelfLife === null || def.shelfLife <= 0)
        return 0;
    return Math.max(0, (tick - stack.gatheredAt) / def.shelfLife);
}
export function freshnessOf(def, stack, tick) {
    const age = ageOf(def, stack, tick);
    for (const stage of FRESHNESS_STAGES) {
        if (age <= stage.until)
            return stage.id;
    }
    return 'spent';
}
/** Past its shelf life entirely. M4-03: expired items examine as spent and do nothing. */
export function isSpent(def, stack, tick) {
    return freshnessOf(def, stack, tick) === 'spent';
}
/** Handy shelf lives, so content spells them in days rather than in ticks. */
export const DAYS = TICKS_PER_GAME_DAY;
/* -------------------------------------------------------------------------- *
 * Carrying
 * -------------------------------------------------------------------------- */
/**
 * Put things in. Returns how many actually went in.
 *
 * Stackable things merge with an existing row of the same kind; everything
 * else takes a row per item. Merging keeps the older gather date — see
 * `ItemStack.gatheredAt`.
 */
export function addItem(inventory, defId, count, tick, stackable = true, note, preserveAge = false) {
    if (count <= 0)
        return 0;
    // A thing with a story of its own never merges: see `ItemStack.note`.
    const merges = stackable && note === undefined;
    if (merges) {
        const existing = inventory.items.find((stack) => stack.defId === defId &&
            stack.note === undefined &&
            (!preserveAge || stack.gatheredAt === tick));
        if (existing) {
            existing.count += count;
            existing.gatheredAt = Math.min(existing.gatheredAt, tick);
            return count;
        }
    }
    if (merges) {
        inventory.items.push({ defId, count, gatheredAt: tick });
    }
    else {
        for (let i = 0; i < count; i += 1) {
            inventory.items.push({
                defId,
                count: 1,
                gatheredAt: tick,
                ...(note === undefined ? {} : { note }),
            });
        }
    }
    return count;
}
/** Opaque, snapshot-safe identity for one current inventory row. */
export function stackKey(stack, index) {
    return `${index}:${stack.defId}:${stack.gatheredAt}`;
}
/** Resolve a client-selected row without ever trusting its item id or count. */
export function stackIndexForKey(inventory, key) {
    return inventory.items.findIndex((stack, index) => stackKey(stack, index) === key);
}
/** Take things out. Returns how many were actually there to take. */
export function removeItem(inventory, defId, count) {
    let wanted = count;
    let taken = 0;
    // Oldest first: using the moss that is about to turn before the moss that is
    // not is what a person would do, and it means a satchel does not silently
    // accumulate a spent row under a fresh one.
    const rows = inventory.items
        .map((stack, index) => ({ stack, index }))
        .filter((row) => row.stack.defId === defId)
        .sort((a, b) => a.stack.gatheredAt - b.stack.gatheredAt);
    for (const row of rows) {
        if (wanted <= 0)
            break;
        const took = Math.min(row.stack.count, wanted);
        row.stack.count -= took;
        wanted -= took;
        taken += took;
    }
    inventory.items = inventory.items.filter((stack) => stack.count > 0);
    return taken;
}
/**
 * The stack of this kind that `removeItem` would spend next — the oldest one.
 *
 * Exists so a rule can ask "is what I am about to use *spent*" without
 * reimplementing the oldest-first order that `removeItem` uses, which is the
 * kind of duplicated rule that drifts silently: a check that looked at the
 * newest stack would happily bless a satchel whose next poultice is a fortnight
 * of mould.
 */
export function oldestStack(inventory, defId) {
    let oldest = null;
    for (const stack of inventory.items) {
        if (stack.defId !== defId || stack.count <= 0)
            continue;
        if (oldest === null || stack.gatheredAt < oldest.gatheredAt)
            oldest = stack;
    }
    return oldest;
}
/**
 * Is the next one of these out of the satchel past its shelf life (M4-03)?
 *
 * `true` for something the satchel does not hold at all, which is the answer
 * that fails safe: a rule asking this is about to use the thing.
 */
export function nextIsSpent(inventory, def, tick) {
    const stack = oldestStack(inventory, def.id);
    return stack === null || isSpent(def, stack, tick);
}
export function countOf(inventory, defId) {
    let total = 0;
    for (const stack of inventory.items) {
        if (stack.defId === defId)
            total += stack.count;
    }
    return total;
}
/** Grams carried, given a way to look definitions up. */
export function totalWeight(inventory, define) {
    let total = 0;
    for (const stack of inventory.items) {
        const def = define(stack.defId);
        if (def)
            total += def.weight * stack.count;
    }
    return total;
}
/**
 * Grams as a player should read them.
 *
 * Kilograms once it is worth saying so, and never more than one decimal:
 * "1.4 kg" is a weight and "1.437 kg" is a spreadsheet. Design pillar — the
 * game shows no numbers it does not mean.
 */
export function formatWeight(grams) {
    if (grams < 1000)
        return `${Math.round(grams)} g`;
    return `${(grams / 1000).toFixed(1)} kg`;
}
export function createLore() {
    return { known: [], forage: 0, tracking: 0, medicine: 0, remedies: [] };
}
/** Where familiarity stops mattering. Past this, practice is its own reward. */
export const MAX_FORAGE_FAMILIARITY = 60;
/**
 * The yield bonus familiarity buys, as a fraction of a whole extra pick.
 *
 * Caps at +1: a practised forager reliably gets two where a novice gets one.
 * Deliberately not a multiplier — a tenfold expert would turn a meadow into a
 * warehouse, and scarcity is the point of the regrowth clock.
 */
export function familiarityBonus(lore) {
    return Math.min(1, lore.forage / MAX_FORAGE_FAMILIARITY);
}
/* -------------------------------------------------------------------------- *
 * Medicine familiarity (M4-01)
 * -------------------------------------------------------------------------- */
/** Where medicine familiarity stops mattering. Mirrors `forage`'s cap. */
export const MAX_MEDICINE_FAMILIARITY = 60;
/**
 * The tick every additional treatment die is earned at, in familiarity.
 *
 * A whole die rather than `forage`'s fractional pick, because a treatment roll
 * is a handful of dice and a fractional one is not a thing the dice can spend.
 * The steps are coarse on purpose — +1 at the halfway mark, +2 at the cap — so
 * that "an experienced treater outperforms a novice" (M4's exit criterion) is a
 * difference a playtester feels rather than a rounding error.
 */
export const MEDICINE_DIE_STEP = 30;
/** Dice a character's medicine familiarity is worth on a treatment roll. */
export function medicineDiceBonus(lore) {
    const medicine = lore?.medicine ?? 0;
    return Math.floor(medicine / MEDICINE_DIE_STEP);
}
/**
 * Practice, capped — the write half of the counter above.
 *
 * `+1` for a treatment that did something (the caller decides what "something"
 * is, so a no-op does not teach). `+2` when a **more familiar treater is at the
 * patient's side**: the teaching-transfer stub the ticket asks for, and it is
 * genuinely one comparison — co-op mentoring bought for a line of code. The
 * mentor learns nothing extra from the pairing, because they are the one who
 * already knows.
 */
export function growMedicine(lore, mentored) {
    const medicine = lore.medicine ?? 0;
    if (medicine >= MAX_MEDICINE_FAMILIARITY)
        return;
    lore.medicine = Math.min(MAX_MEDICINE_FAMILIARITY, medicine + (mentored ? 2 : 1));
}
/* -------------------------------------------------------------------------- *
 * Walking on what is left (M4-05)
 * -------------------------------------------------------------------------- */
/**
 * The tag a thing carries when it can be leant on — a carved crutch, the
 * smith's peg-leg (M5-01).
 *
 * A tag rather than an item id, so that the rule is "something to walk with"
 * and content decides what qualifies. The *number* is on the definition, in
 * `nutrition`'s spirit: how much of a leg a given aid gives back is a fact
 * about that aid.
 */
export const MOBILITY_TAG = 'mobility';
/**
 * How fast the best aid in this satchel lets a one-legged character walk, as a
 * fraction of full speed — 0 when there is nothing to lean on. Mobility aids
 * are passive carried tools; the equip verb is reserved for combat weapons.
 *
 * Read off `dressingValue`'s pattern: a scored best-of rather than a ranked
 * list of ids, so the day the smith sells something better than a crutch it
 * simply wins. The aid is *carried* rather than equipped because there is no
 * equipping in the MVP and a crutch under your arm is not gear you fight with:
 * it is a thing in your hands, and losing your satchel to a robber losing you
 * the ability to walk is a story the game is entitled to tell.
 */
export function mobilityAid(inventory, define) {
    if (!inventory)
        return 0;
    let best = 0;
    for (const stack of inventory.items) {
        if (stack.count <= 0)
            continue;
        const def = define(stack.defId);
        if (!def?.tags.includes(MOBILITY_TAG))
            continue;
        best = Math.max(best, def.mobility ?? 0);
    }
    return best;
}
/**
 * Learn what a herb *does*, having just watched it do it (M4-03).
 *
 * Idempotent and sorted, exactly like `learn`. Called only from the point where
 * a treatment visibly worked, which is what keeps the knowledge honest: reading
 * about willow bark teaches nobody anything, and chewing it on a day your leg
 * hurts teaches you the whole of it.
 */
export function learnRemedy(lore, defId) {
    const remedies = (lore.remedies ??= []);
    if (remedies.includes(defId))
        return false;
    remedies.push(defId);
    remedies.sort();
    return true;
}
/** Has this character seen what that herb does? */
export function knowsRemedy(lore, defId) {
    return lore?.remedies?.includes(defId) === true;
}
export function knows(lore, defId) {
    return lore !== undefined && lore.known.includes(defId);
}
/** Learn something. Idempotent — knowing a plant twice is knowing it. */
export function learn(lore, defId) {
    if (lore.known.includes(defId))
        return false;
    lore.known.push(defId);
    // Sorted, so world state hashes the same however the knowledge was acquired.
    lore.known.sort();
    return true;
}
/** What to call a thing, given who is looking at it. */
export function nameFor(def, lore) {
    return knows(lore, def.id) ? def.name : def.unknownName;
}
export function examineFor(def, lore) {
    return knows(lore, def.id) ? def.examine : def.unknownExamine;
}
//# sourceMappingURL=item.js.map